import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  FileDownloadOutlined,
  VisibilityOutlined,
  TrendingUp,
  WarningAmberRounded,
  AccountBalanceWalletOutlined,
  EditOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { currencySymbol } from 'utils/constant';
import MainContainer from 'theme/MainConatiner';
import HeaderTilteBar from 'components/TitleBar/HeaderTilteBar';
import CustomFilterButton from 'components/Fileds/CustomFilterButton';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomButton from 'components/Fileds/CustomButton';
import CommonDataGrid from 'components/TableListing/CommonTable';
import NoItemsCreated from 'components/NoItemsCreated/NoItemsCreated';
import NoSearchFound from 'components/NoSearchFound/NoSearchFound';
import ShiftDetailModal from './components/ShiftDetailModal';
import CashDateRangePicker from './components/CashDateRangePicker';
import ReportsSkeleton from './components/ReportsSkeleton';
import AdjustWalletModal from './components/AdjustWalletModal';
import './style.css';

const CashManagementContainer = ({
  // Tab control
  activeTab,
  setActiveTab,

  // Tab 1: Current Shift
  runningShifts = [],
  runningLoading,
  runningSearch,
  setRunningSearch,
  runningPaginationModel,
  setRunningPaginationModel,
  runningTotalCount,

  // Tab 2: Cash History
  historyShifts = [],
  historyLoading,
  historySearch,
  setHistorySearch,
  historyStatus,
  setHistoryStatus,
  historyStaff,
  setHistoryStaff,
  historyDateRange,
  setHistoryDateRange,
  historyPaginationModel,
  setHistoryPaginationModel,
  historyTotalCount,
  staffOptions = [],
  handleExportCsv,
  exportLoading,

  // Tab 3: Reports
  reportsData,
  reportsLoading,
  reportsStaff,
  setReportsStaff,
  reportsDateRange,
  setReportsDateRange,

  // Restaurant Cash Wallet
  walletBalance = 0,
  walletLoading = false,
  refetchWallet,

  // Tab 4: Wallet Transactions (Screen view)
  transactionSearch = '',
  setTransactionSearch,
  transactionType = 'all',
  setTransactionType,
  transactionDirection = 'all',
  setTransactionDirection,
  transactionDateRange,
  setTransactionDateRange,
  transactionPaginationModel,
  setTransactionPaginationModel,
  transactionsList = [],
  transactionSummary = {},
  transactionsLoading = false,
  transactionTotalCount = 0,
  previousTab = 'current_shift',
  setPreviousTab,

  // Modals
  selectedShiftId,
  setSelectedShiftId,
  modalOpen,
  setModalOpen,
  autoPrint,
  setAutoPrint,
}) => {
  const [selectedShiftRow, setSelectedShiftRow] = React.useState(null);

  // Wallet Modal states
  const [adjustModalOpen, setAdjustModalOpen] = React.useState(false);

  // Get logged-in POS user data
  const posData = useSelector((state) => state?.posData);
  const vendorCurrency = posData?.vendor?.currency;
  const currency =
    currencySymbol(vendorCurrency) ||
    (vendorCurrency === 'USD' ? '$' : vendorCurrency === 'EUR' ? '€' : vendorCurrency) ||
    '€';

  // Helper to format date & time reliably across all backend timestamp schemas
  const formatDateTime = (row) => {
    if (row?.created_at_formatted) return row.created_at_formatted;
    if (row?.date_formatted) return row.date_formatted;

    const rawDate =
      row?.created_at ||
      row?.createdAt ||
      row?.transaction_date ||
      row?.date ||
      row?.timestamp ||
      row?.datetime ||
      row?.updated_at ||
      row?.updatedAt;

    if (!rawDate) return '-';

    const d = dayjs(typeof rawDate === 'string' ? rawDate.replace(' ', 'T') : rawDate);
    if (d.isValid()) {
      return d.format('MMM D, YYYY, h:mm A');
    }

    try {
      const nativeDate = new Date(rawDate);
      if (!isNaN(nativeDate.getTime())) {
        return nativeDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
      }
    } catch (e) {
      // ignore
    }

    return '-';
  };

  // Helper to resolve staff name and identify if it is the currently logged-in user
  const getStaffInfo = (row) => {
    const loggedInId =
      posData?.id ??
      posData?._id ??
      posData?.user_id ??
      posData?.userId ??
      posData?.vendor?.user_id;

    const rowId =
      row?.user?.id ??
      row?.user?._id ??
      row?.user_id ??
      row?.userId ??
      row?.staff?.id ??
      row?.staff?._id ??
      row?.staff_id ??
      row?.staffId ??
      row?.created_by ??
      row?.admin_id;

    const loggedInName = (posData?.full_name || posData?.name || '').trim();

    let rawName = (
      row?.user?.name ||
      row?.user?.full_name ||
      row?.staff?.name ||
      row?.staff?.full_name ||
      row?.staff_name ||
      row?.user_name ||
      row?.created_by_name ||
      ''
    ).trim();

    const isMatchById =
      loggedInId != null &&
      rowId != null &&
      String(loggedInId) === String(rowId);

    const isMatchByEmail = Boolean(
      posData?.email &&
      (row?.user?.email || row?.email) &&
      posData.email.toLowerCase() === (row?.user?.email || row?.email).toLowerCase()
    );

    const isMatchByName = Boolean(
      loggedInName &&
      rawName &&
      rawName.toLowerCase() === loggedInName.toLowerCase()
    );

    const isCurrent = Boolean(isMatchById || isMatchByEmail || isMatchByName);

    if (!rawName) {
      rawName = isCurrent ? (loggedInName || 'Staff') : 'Staff';
    }

    const cleanedName = rawName.replace(/\s*\((you|You)\)$/i, '');

    return {
      name: cleanedName,
      isCurrent,
    };
  };

  const handleViewShift = (rowOrId, shouldPrint = false) => {
    if (rowOrId && typeof rowOrId === 'object') {
      setSelectedShiftId(rowOrId.id);
      setSelectedShiftRow(rowOrId);
    } else {
      setSelectedShiftId(rowOrId);
      setSelectedShiftRow(null);
    }
    setAutoPrint(shouldPrint);
    setModalOpen(true);
  };

  const statusFilterOptions = [
    { value: 'all', label: 'All statuses' },
    { value: 'open', label: 'Opened' },
    { value: 'closed', label: 'Closed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'with_variance', label: 'With Variance' },
    { value: 'balanced', label: 'Balanced' },
  ];

  const transactionTypeFilterOptions = [
    { value: 'all', label: 'All types' },
    { value: 'shift_open_float', label: 'Opening Float Taken' },
    { value: 'shift_close_deposit', label: 'Shift Close Deposit' },
    { value: 'manual_addition', label: 'Manual Cash Added' },
    { value: 'manual_subtraction', label: 'Manual Cash Deducted' },
    { value: 'manual_direct_adjust', label: 'Direct Balance Adjust' },
  ];

  const transactionDirectionOptions = [
    { value: 'all', label: 'All directions' },
    { value: 'credit', label: 'Inflow (+) Credits' },
    { value: 'debit', label: 'Outflow (-) Debits' },
  ];

  const TYPE_BADGE_CONFIG = {
    shift_open_float: {
      label: 'Opening Float Taken',
      color: '#1D4ED8',
      bgcolor: '#EFF6FF',
      borderColor: '#BFDBFE',
    },
    shift_close_deposit: {
      label: 'Shift Close Deposit',
      color: '#047857',
      bgcolor: '#ECFDF5',
      borderColor: '#A7F3D0',
    },
    manual_addition: {
      label: 'Manual Cash Added',
      color: '#059669',
      bgcolor: '#ECFDF5',
      borderColor: '#A7F3D0',
    },
    manual_subtraction: {
      label: 'Manual Cash Deducted',
      color: '#B91C1C',
      bgcolor: '#FEF2F2',
      borderColor: '#FECACA',
    },
    manual_direct_adjust: {
      label: 'Direct Balance Adjust',
      color: '#6D28D9',
      bgcolor: '#F5F3FF',
      borderColor: '#DDD6FE',
    },
  };

  // Columns for Tab 4: Wallet Transactions
  const transactionColumns = [
    {
      field: 'created_at',
      headerName: 'Date & Time',
      flex: 1.3,
      minWidth: 160,
      valueGetter: (params) => {
        const row = params?.row || params;
        return (
          row?.created_at ||
          row?.createdAt ||
          row?.transaction_date ||
          row?.date ||
          row?.timestamp ||
          ''
        );
      },
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px', fontSize: '13px' }}>
          {formatDateTime(row)}
        </Box>
      ),
    },
    {
      field: 'transaction_type',
      headerName: 'Type',
      flex: 1.4,
      minWidth: 175,
      renderCell: ({ row }) => {
        const typeCfg = TYPE_BADGE_CONFIG[row.transaction_type] || {
          label: row.type_label || 'Adjustment',
          color: '#374151',
          bgcolor: '#F3F4F6',
          borderColor: '#E5E7EB',
        };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', paddingLeft: '10px' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 10px',
                borderRadius: '16px',
                fontSize: '11px',
                fontWeight: 600,
                color: typeCfg.color,
                bgcolor: typeCfg.bgcolor,
                border: `1px solid ${typeCfg.borderColor}`,
                lineHeight: '18px',
                whiteSpace: 'nowrap',
              }}
            >
              {typeCfg.label}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      minWidth: 110,
      renderCell: ({ row }) => {
        const isCredit = row.direction === 'credit' || row.is_credit;
        const amt = parseFloat(row.amount || 0);
        return (
          <Box
            sx={{
              paddingLeft: '10px',
              fontWeight: 700,
              fontSize: '13.5px',
              color: isCredit ? '#047857' : '#B91C1C',
            }}
          >
            {isCredit ? '+' : '-'}
            {currency}
            {amt.toFixed(2)}
          </Box>
        );
      },
    },
    {
      field: 'balance_after',
      headerName: 'Balance After',
      flex: 1,
      minWidth: 115,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px', fontWeight: 600, color: '#111827' }}>
          {currency}
          {parseFloat(row.balance_after || 0).toFixed(2)}
        </Box>
      ),
    },
    {
      field: 'staff',
      headerName: 'Staff',
      flex: 1.1,
      minWidth: 120,
      renderCell: ({ row }) => {
        const { name, isCurrent } = getStaffInfo(row);
        return (
          <Box sx={{ paddingLeft: '10px', fontWeight: 500, color: '#111827' }}>
            {name}
            {isCurrent && (
              <Typography
                component="span"
                sx={{
                  color: '#4B5563',
                  fontSize: '12px',
                  fontWeight: 500,
                  ml: 0.5,
                }}
              >
                (you)
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'shift',
      headerName: 'Shift',
      flex: 1.3,
      minWidth: 140,
      renderCell: ({ row }) => {
        if (!row.shift) {
          return <Box sx={{ paddingLeft: '10px', color: '#9CA3AF' }}>-</Box>;
        }
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%',
              paddingLeft: '10px',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', fontSize: '13px' }}>
              {row.shift.shift_name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              {row.shift.shift_code || `SH-${row.shift.id}`}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1.6,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            paddingLeft: '10px',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: row.notes ? '#374151' : '#9CA3AF',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.notes || '-'}
          </Typography>
        </Box>
      ),
    },
  ];

  // Columns for Tab 1: Current Shift (Running shifts)
  const runningColumns = [
    {
      field: 'shift_name',
      headerName: 'Shift',
      flex: 1.4,
      minWidth: 170,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            paddingLeft: '10px',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
            {row.shift_name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            {row.shift_code || `SH-${row.id >= 1000 ? row.id : 1000 + row.id}`}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'opened_at_formatted',
      headerName: 'Opened',
      flex: 1.4,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px' }}>
          {row.opened_at_formatted || (row.opened_at ? new Date(row.opened_at).toLocaleString() : '-')}
        </Box>
      ),
    },
    {
      field: 'staff',
      headerName: 'Staff',
      flex: 1.1,
      minWidth: 120,
      renderCell: ({ row }) => {
        const { name, isCurrent } = getStaffInfo(row);
        return (
          <Box sx={{ paddingLeft: '10px', fontWeight: 500, color: '#111827' }}>
            {name}
            {isCurrent && (
              <Typography
                component="span"
                sx={{
                  color: '#4B5563',
                  fontSize: '12px',
                  fontWeight: 500,
                  ml: 0.5,
                }}
              >
                (you)
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'opening_float',
      headerName: 'Opening Float',
      flex: 1,
      minWidth: 110,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px' }}>
          {row.opening_float_formatted || `€${parseFloat(row.opening_float || 0).toFixed(2)}`}
        </Box>
      ),
    },
    {
      field: 'cash_sales',
      headerName: 'Cash Sales',
      flex: 1,
      minWidth: 110,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px', color: '#059669', fontWeight: 600 }}>
          {row.cash_sales_formatted || `€${parseFloat(row.cash_sales || 0).toFixed(2)}`}
        </Box>
      ),
    },
    {
      field: 'cash_in_out',
      headerName: 'Cash In / Out',
      flex: 1.2,
      minWidth: 130,
      renderCell: ({ row }) => {
        const cIn = row.cash_in_formatted || `€${parseFloat(row.cash_in || row.cash_in_total || 0).toFixed(2)}`;
        const cOut = row.cash_out_formatted || `€${parseFloat(row.cash_out || row.cash_out_total || 0).toFixed(2)}`;
        return <Box sx={{ paddingLeft: '10px' }}>{`${cIn} / ${cOut}`}</Box>;
      },
    },
    {
      field: 'expected_cash',
      headerName: 'Expected Cash',
      flex: 1.1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px', fontWeight: 700, color: '#111827' }}>
          {row.expected_cash_formatted || `€${parseFloat(row.expected_cash || 0).toFixed(2)}`}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      minWidth: 100,
      renderCell: ({ row }) => {
        const statusUpper = (row.status || 'OPEN').toUpperCase();
        const isOpen = statusUpper === 'OPEN';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', paddingLeft: '10px' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3px 12px',
                borderRadius: '16px',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                bgcolor: isOpen ? '#ECFDF5' : '#ffffff',
                color: isOpen ? '#047857' : '#374151',
                border: `1px solid ${isOpen ? '#A7F3D0' : '#D1D5DB'}`,
                lineHeight: '18px',
              }}
            >
              {statusUpper}
            </Box>
          </Box>
        );
      },
    },
  ];

  // Columns for Tab 2: Cash History
  const historyColumns = [
    {
      field: 'shift_name',
      headerName: 'Shift',
      flex: 1.4,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            paddingLeft: '10px',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
            {row.shift_name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            {row.shift_code || `SH-${row.id >= 1000 ? row.id : 1000 + row.id}`}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'opened_at_formatted',
      headerName: 'Opened',
      flex: 1.4,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px' }}>
          {row.opened_at_formatted || (row.opened_at ? new Date(row.opened_at).toLocaleString() : '-')}
        </Box>
      ),
    },
    {
      field: 'staff_name',
      headerName: 'Staff',
      flex: 1,
      minWidth: 110,
      renderCell: ({ row }) => {
        const { name, isCurrent } = getStaffInfo(row);
        return (
          <Box sx={{ paddingLeft: '10px', fontWeight: 500, color: '#111827' }}>
            {name}
            {isCurrent && (
              <Typography
                component="span"
                sx={{
                  color: '#4B5563',
                  fontSize: '12px',
                  fontWeight: 500,
                  ml: 0.5,
                }}
              >
                (you)
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'opening_float',
      headerName: 'Opening',
      flex: 0.9,
      minWidth: 95,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px' }}>
          {row.opening_float_formatted || `€${parseFloat(row.opening_float || 0).toFixed(2)}`}
        </Box>
      ),
    },
    {
      field: 'expected_cash',
      headerName: 'Expected',
      flex: 0.9,
      minWidth: 95,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px' }}>
          {row.expected_cash_formatted || `€${parseFloat(row.expected_cash || 0).toFixed(2)}`}
        </Box>
      ),
    },
    {
      field: 'actual_cash',
      headerName: 'Actual',
      flex: 0.9,
      minWidth: 95,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px', fontWeight: 500 }}>
          {row.actual_cash_formatted || (row.actual_cash != null ? `€${parseFloat(row.actual_cash).toFixed(2)}` : '-')}
        </Box>
      ),
    },
    {
      field: 'variance',
      headerName: 'Variance',
      flex: 1.3,
      minWidth: 140,
      renderCell: ({ row }) => {
        const v = parseFloat(row.variance ?? row.cash_variance);
        if (isNaN(v)) return <Box sx={{ paddingLeft: '10px' }}>-</Box>;
        if (v > 0) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', paddingLeft: '10px' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '3px 10px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: 600,
                  bgcolor: '#FEF3C7',
                  color: '#B45309',
                  border: '1px solid #FDE68A',
                  lineHeight: '18px',
                }}
              >
                {row.variance_label || `Surplus +€${v.toFixed(2)}`}
              </Box>
            </Box>
          );
        }
        if (v < 0) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', paddingLeft: '10px' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '3px 10px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: 600,
                  bgcolor: '#FEE2E2',
                  color: '#B91C1C',
                  border: '1px solid #FECACA',
                  lineHeight: '18px',
                }}
              >
                {row.variance_label || `Shortage -€${Math.abs(v).toFixed(2)}`}
              </Box>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', paddingLeft: '10px' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 10px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: 600,
                bgcolor: '#ECFDF5',
                color: '#047857',
                border: '1px solid #A7F3D0',
                lineHeight: '18px',
              }}
            >
              ✓ Balanced
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'closing_float',
      headerName: 'Closing Float',
      flex: 1,
      minWidth: 110,
      renderCell: ({ row }) => (
        <Box sx={{ paddingLeft: '10px' }}>
          {row.closing_float_formatted || `€${parseFloat(row.closing_float || 0).toFixed(2)}`}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      minWidth: 100,
      renderCell: ({ row }) => {
        const statusUpper = (row.status || 'CLOSED').toUpperCase();
        const isClosed = statusUpper === 'CLOSED';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', paddingLeft: '10px' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3px 12px',
                borderRadius: '16px',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                bgcolor: isClosed ? '#ffffff' : '#ECFDF5',
                color: isClosed ? '#374151' : '#047857',
                border: `1px solid ${isClosed ? '#D1D5DB' : '#A7F3D0'}`,
                lineHeight: '18px',
              }}
            >
              {statusUpper}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.9,
      minWidth: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1.5, paddingLeft: '10px' }}>
          <VisibilityOutlined
            fontSize="small"
            sx={{ color: '#1297DE', cursor: 'pointer' }}
            onClick={() => handleViewShift(row, false)}
          />
        </Box>
      ),
    },
  ];

  // Reports data unpack
  const kpi = reportsData?.kpi_summary || {};
  const staffVarianceSummary = reportsData?.staff_variance_summary || [];

  const isCurrentSearching = runningSearch.trim().length > 0;
  const isHistorySearching = historySearch.trim().length > 0;

  // Wallet Balance + Edit Icon + Transactions Button UI rendered before Search
  const walletAndTransactionsUI = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
      {/* Wallet Balance Badge with Edit Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
          height: '36px',
          bgcolor: '#FFFFFF',
          border: '1px solid #D1D5DB',
          borderRadius: '8px',
          px: 1.2,
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.04)',
          whiteSpace: 'nowrap',
          transition: 'all 0.15s ease',
          '&:hover': {
            borderColor: '#9CA3AF',
          },
        }}
      >
        <AccountBalanceWalletOutlined sx={{ fontSize: 18, color: '#4B5563' }} />
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontSize: '12.5px', color: '#6B7280', fontWeight: 500 }}>
            Wallet Balance:
          </Typography>
          <Typography sx={{ fontSize: '13.5px', color: '#111827', fontWeight: 700 }}>
            {walletLoading ? (
              <CircularProgress size={12} sx={{ color: '#111827', ml: 0.5 }} />
            ) : (
              `${currency}${parseFloat(walletBalance || 0).toFixed(2)}`
            )}
          </Typography>
        </Box>

        {/* Edit Icon Button */}
        <Tooltip title="Adjust Wallet Balance" arrow>
          <IconButton
            size="small"
            onClick={() => setAdjustModalOpen(true)}
            sx={{
              p: '3px',
              ml: 0.3,
              borderRadius: '6px',
              color: '#1297DE',
              bgcolor: '#F0F9FF',
              border: '1px solid #BAE6FD',
              '&:hover': {
                bgcolor: '#E0F2FE',
                borderColor: '#7DD3FC',
                color: '#0284C7',
              },
            }}
          >
            <EditOutlined sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Transactions Button */}
      <CustomButton
        variant={activeTab === 'transactions' ? 'contained' : 'outlined'}
        onClick={() => {
          if (activeTab !== 'transactions') {
            setPreviousTab(activeTab);
            setActiveTab('transactions');
          }
        }}
        startIcon={<ReceiptLongOutlined sx={{ fontSize: 17 }} />}
        sx={{
          height: '36px',
          borderRadius: '8px',
          backgroundColor: activeTab === 'transactions' ? '#000000' : '#FFFFFF',
          color: activeTab === 'transactions' ? '#FFFFFF' : '#111827',
          border: activeTab === 'transactions' ? '1px solid #000000' : '1px solid #D1D5DB',
          fontWeight: 600,
          fontSize: '13px',
          px: 1.5,
          whiteSpace: 'nowrap',
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.04)',
          '&:hover': {
            backgroundColor: activeTab === 'transactions' ? '#1f2937' : '#F9FAFB',
            borderColor: activeTab === 'transactions' ? '#1f2937' : '#9CA3AF',
          },
        }}
      >
        Transactions
      </CustomButton>
    </Box>
  );

  return (
    <MainContainer>
      <Card className="main-card-container" sx={{ padding: '0px !important' }}>
        {/* Row 1: HeaderTilteBar with Screen Title, Inline Tabs (like Manage Accounts), and Search */}
        <HeaderTilteBar
          title={activeTab === 'transactions' ? 'Transactions' : 'Cash Management'}
          onBack={activeTab === 'transactions' ? () => setActiveTab(previousTab || 'current_shift') : undefined}
          isSearch={activeTab !== 'reports'}
          isAddVisible={false}
          searchQuery={
            activeTab === 'current_shift'
              ? runningSearch
              : activeTab === 'history'
              ? historySearch
              : transactionSearch
          }
          onSearchChange={
            activeTab === 'current_shift'
              ? (val) => {
                  setRunningSearch(val);
                  setRunningPaginationModel((prev) => ({ ...prev, page: 0 }));
                }
              : activeTab === 'history'
              ? (val) => {
                  setHistorySearch(val);
                  setHistoryPaginationModel((prev) => ({ ...prev, page: 0 }));
                }
              : (val) => {
                  setTransactionSearch(val);
                  setTransactionPaginationModel((prev) => ({ ...prev, page: 0 }));
                }
          }
          searchPlaceholder={activeTab === 'transactions' ? 'Search with Notes...' : 'Search with Name'}
          searchWidth={{ xs: '100%', sm: 175, md: 195, lg: 215 }}
          beforeSearchContent={walletAndTransactionsUI}
          rightContent={null}
        >
          {/* Tab Filter Buttons styled like Manage Accounts (Image 2 & 4) */}
          <Box sx={{ display: 'flex', gap: '8px', ml: { xs: 1, sm: 1.5 } }}>
            <CustomFilterButton
              label="Current Shift"
              color="#000000"
              backgroundColor="#000000"
              selected={activeTab === 'current_shift'}
              onClick={() => setActiveTab('current_shift')}
              height="32px"
              sx={{ px: 1.5, fontSize: '13px', whiteSpace: 'nowrap' }}
            />
            <CustomFilterButton
              label="Shift History"
              color="#000000"
              backgroundColor="#000000"
              selected={activeTab === 'history'}
              onClick={() => setActiveTab('history')}
              height="32px"
              sx={{ px: 1.5, fontSize: '13px', whiteSpace: 'nowrap' }}
            />
            <CustomFilterButton
              label="Reports"
              color="#000000"
              backgroundColor="#000000"
              selected={activeTab === 'reports'}
              onClick={() => setActiveTab('reports')}
              height="32px"
              sx={{ px: 1.5, fontSize: '13px', whiteSpace: 'nowrap' }}
            />
          </Box>
        </HeaderTilteBar>

        {/* ═════════════════ TAB 1: CURRENT SHIFT ═════════════════ */}
        {activeTab === 'current_shift' && (
          <>
            {runningShifts.length === 0 && !runningLoading ? (
              <Box className="body-wrapper" py={6}>
                {isCurrentSearching ? (
                  <NoSearchFound
                    title="No running shifts match your search"
                    subtitle="Please try again with a different keyword."
                  />
                ) : (
                  <NoItemsCreated
                    title="No Running Shifts Open Yet!"
                    subtitle="Active cash shifts will appear here once opened by staff."
                  />
                )}
              </Box>
            ) : (
              <Box>
                <CommonDataGrid
                  columns={runningColumns}
                  rows={runningShifts}
                  rowCount={runningTotalCount}
                  loading={runningLoading}
                  paginationModel={runningPaginationModel}
                  setPaginationModel={setRunningPaginationModel}
                />
              </Box>
            )}
          </>
        )}

        {/* ═════════════════ TAB 2: CASH HISTORY ═════════════════ */}
        {activeTab === 'history' && (
          <>
            {/* Row 2: Secondary Filters Row (All statuses, All staff on left; Export CSV on right) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                mt: 0.5,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {/* Left: Dropdown filters & Date Range Picker */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ width: 160 }}>
                  <CustomSelect
                    name="status"
                    options={statusFilterOptions}
                    value={historyStatus}
                    onChange={(e) => {
                      setHistoryStatus(e.target.value);
                      setHistoryPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                      fontSize: '14px',
                      height: '40px',
                    }}
                  />
                </Box>

                <Box sx={{ width: 160 }}>
                  <CustomSelect
                    name="staff"
                    options={[
                      { value: 'all', label: 'All staff' },
                      ...staffOptions.map((s) => ({
                        value: s.id,
                        label: s.name || s.full_name || 'Staff',
                      })),
                    ]}
                    value={historyStaff}
                    onChange={(e) => {
                      setHistoryStaff(e.target.value);
                      setHistoryPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                      fontSize: '14px',
                      height: '40px',
                    }}
                  />
                </Box>

                <CashDateRangePicker
                  dateRange={historyDateRange}
                  onDateChange={(range) => {
                    setHistoryDateRange(range);
                    setHistoryPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                  placeholder="Filter by Date"
                />
              </Box>

              {/* Right: Export CSV Button */}
              <Box>
                <CustomButton
                  variant="outlined"
                  onClick={handleExportCsv}
                  loading={exportLoading}
                  startIcon={<FileDownloadOutlined />}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    border: '1px solid #D1D5DB',
                    height: '40px',
                    px: 2.5,
                    fontWeight: 600,
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                      borderColor: '#9CA3AF',
                    },
                  }}
                >
                  Export CSV
                </CustomButton>
              </Box>
            </Box>

            {/* Table Listing */}
            {historyShifts.length === 0 && !historyLoading ? (
              <Box className="body-wrapper" py={6}>
                {isHistorySearching || historyStatus !== 'all' || historyStaff !== 'all' ? (
                  <NoSearchFound
                    title="No cash history records match your filters"
                    subtitle="Please try again with different keywords or filter criteria."
                  />
                ) : (
                  <NoItemsCreated
                    title="No Cash Shift History Yet!"
                    subtitle="Completed till reconciliation records will appear here."
                  />
                )}
              </Box>
            ) : (
              <Box>
                <CommonDataGrid
                  columns={historyColumns}
                  rows={historyShifts}
                  rowCount={historyTotalCount}
                  loading={historyLoading}
                  paginationModel={historyPaginationModel}
                  setPaginationModel={setHistoryPaginationModel}
                />
              </Box>
            )}
          </>
        )}

        {/* ═════════════════ TAB 3: REPORTS ═════════════════ */}
        {activeTab === 'reports' && (
          <Box sx={{ width: '100%' }}>
            {/* Row 2: Secondary Filter for Reports (Staff Dropdown & Date Range Picker) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2, mt: 0.5 }}>
              <Box sx={{ width: 180 }}>
                <CustomSelect
                  name="reportsStaff"
                  options={[
                    { value: 'all', label: 'All staff' },
                    ...staffOptions.map((s) => ({
                      value: s.id,
                      label: s.name || s.full_name || 'Staff',
                    })),
                  ]}
                  value={reportsStaff}
                  onChange={(e) => setReportsStaff(e.target.value)}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    height: '40px',
                  }}
                />
              </Box>

              <CashDateRangePicker
                dateRange={reportsDateRange}
                onDateChange={(range) => {
                  setReportsDateRange(range);
                }}
                placeholder="Filter by Date"
              />
            </Box>

            {reportsLoading ? (
              <ReportsSkeleton />
            ) : (
              <>
                {/* 8 Top KPI Statistics Cards */}
                <Box className="kpi-cards-grid">
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box className="kpi-card">
                        <Typography className="kpi-label">CASH SALES (REVENUE)</Typography>
                        <Typography className="kpi-value green">
                          {kpi.cash_sales_revenue_formatted || kpi.cash_sales_formatted || `€${parseFloat(kpi.cash_sales || 0).toFixed(2)}`}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box className="kpi-card">
                        <Typography className="kpi-label">EXPECTED CASH</Typography>
                        <Typography className="kpi-value">
                          {kpi.expected_cash_formatted || `€${parseFloat(kpi.expected_cash || 0).toFixed(2)}`}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box className="kpi-card">
                        <Typography className="kpi-label">ACTUAL CASH</Typography>
                        <Typography className="kpi-value">
                          {kpi.actual_cash_formatted || `€${parseFloat(kpi.actual_cash || 0).toFixed(2)}`}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box className="kpi-card">
                        <Typography className="kpi-label">CASH REMOVED</Typography>
                        <Typography className="kpi-value">
                          {kpi.cash_removed_formatted || kpi.cash_removal_formatted || `€${parseFloat(kpi.cash_removal || 0).toFixed(2)}`}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box className="kpi-card">
                        <Typography className="kpi-label">OPENING FLOATS</Typography>
                        <Typography className="kpi-value">
                          {kpi.opening_floats_formatted || `€${parseFloat(kpi.opening_floats || 0).toFixed(2)}`}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box className="kpi-card">
                        <Typography className="kpi-label">CASH IN / OUT</Typography>
                        <Typography className="kpi-value">
                          {kpi.cash_in_out_label || kpi.cash_in_out_formatted || `€${parseFloat(kpi.cash_in || 0).toFixed(2)} / €${parseFloat(kpi.cash_out || 0).toFixed(2)}`}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box className="kpi-card">
                        <Typography className="kpi-label">TOTAL SHORTAGE</Typography>
                        <Typography className="kpi-value red">
                          {kpi.total_shortage_formatted || (parseFloat(kpi.total_shortage || 0) < 0 ? `-€${Math.abs(kpi.total_shortage).toFixed(2)}` : '€0.00')}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box className="kpi-card">
                        <Typography className="kpi-label">TOTAL SURPLUS</Typography>
                        <Typography className="kpi-value amber">
                          {kpi.total_surplus_formatted || (parseFloat(kpi.total_surplus || 0) > 0 ? `+€${parseFloat(kpi.total_surplus).toFixed(2)}` : '€0.00')}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Staff Variance Summary Table */}
                <Box className="staff-variance-section">
                  <Box className="staff-variance-header">
                    <TrendingUp sx={{ color: '#4B5563', fontSize: 20 }} />
                    <Typography className="staff-variance-title">Staff variance summary</Typography>
                  </Box>

                  {staffVarianceSummary.length === 0 ? (
                    <Box py={4} textAlign="center">
                      <Typography color="text.secondary">No staff variance records found for the selected staff filter.</Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} elevation={0}>
                      <Table>
                        <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '13px', paddingLeft: '20px' }}>Staff</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '13px' }}>Shifts</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '13px' }}>Shortages</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '13px' }}>Surpluses</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '13px' }}>Net Variance</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: '13px' }}>Largest</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {staffVarianceSummary.map((item, idx) => (
                            <TableRow key={item.staff_id || idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                              <TableCell sx={{ fontWeight: 600, color: '#111827', paddingLeft: '20px' }}>
                                {item.staff}
                              </TableCell>
                              <TableCell>{item.shifts}</TableCell>
                              <TableCell sx={{ color: parseFloat(item.shortages) < 0 ? '#DC2626' : '#111827', fontWeight: 500 }}>
                                {item.shortages_formatted || '€0.00'}
                              </TableCell>
                              <TableCell sx={{ color: parseFloat(item.surpluses) > 0 ? '#D97706' : '#111827', fontWeight: 500 }}>
                                {item.surpluses_formatted || '€0.00'}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#111827' }}>
                                {item.net_variance_formatted || '€0.00'}
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  {item.has_alert && (
                                    <WarningAmberRounded sx={{ color: '#DC2626', fontSize: 18 }} />
                                  )}
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ color: item.has_alert ? '#DC2626' : '#111827' }}
                                  >
                                    {item.largest_variance_formatted || '€0.00'}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}

        {/* ═════════════════ TAB 4: WALLET TRANSACTIONS ═════════════════ */}
        {activeTab === 'transactions' && (
          <Box sx={{ width: '100%' }}>
            {/* Row 2: Secondary Filters Row (Type, Direction, Date Range, and Adjust Button) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                mt: 0.5,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {/* Left: Dropdown filters & Date Range Picker */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ width: 190 }}>
                  <CustomSelect
                    name="transactionType"
                    options={transactionTypeFilterOptions}
                    value={transactionType}
                    onChange={(e) => {
                      setTransactionType(e.target.value);
                      setTransactionPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                      fontSize: '14px',
                      height: '40px',
                    }}
                  />
                </Box>

                <Box sx={{ width: 170 }}>
                  <CustomSelect
                    name="transactionDirection"
                    options={transactionDirectionOptions}
                    value={transactionDirection}
                    onChange={(e) => {
                      setTransactionDirection(e.target.value);
                      setTransactionPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                      fontSize: '14px',
                      height: '40px',
                    }}
                  />
                </Box>

                <CashDateRangePicker
                  dateRange={transactionDateRange}
                  onDateChange={(range) => {
                    setTransactionDateRange(range);
                    setTransactionPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                  placeholder="Filter by Date"
                />
              </Box>
            </Box>

            {/* Row 3: Table Listing */}
            {transactionsList.length === 0 && !transactionsLoading ? (
              <Box className="body-wrapper" py={6}>
                {transactionSearch.trim().length > 0 ||
                transactionType !== 'all' ||
                transactionDirection !== 'all' ||
                transactionDateRange?.startDate ? (
                  <NoSearchFound
                    title="No wallet transactions match your filters"
                    subtitle="Please try again with different keywords or filter criteria."
                  />
                ) : (
                  <NoItemsCreated
                    title="No Wallet Transactions Recorded Yet!"
                    subtitle="Shift open floats, close deposits, and manual balance adjustments will appear here."
                  />
                )}
              </Box>
            ) : (
              <Box>
                <CommonDataGrid
                  columns={transactionColumns}
                  rows={transactionsList}
                  rowCount={transactionTotalCount}
                  loading={transactionsLoading}
                  paginationModel={transactionPaginationModel}
                  setPaginationModel={setTransactionPaginationModel}
                />
              </Box>
            )}
          </Box>
        )}
      </Card>

      {/* Shift Detail / Print Audit Modal */}
      <ShiftDetailModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedShiftRow(null);
        }}
        shiftId={selectedShiftId}
        initialData={selectedShiftRow}
        autoPrint={autoPrint}
      />

      {/* Adjust Wallet Balance Modal */}
      <AdjustWalletModal
        open={adjustModalOpen}
        onClose={() => setAdjustModalOpen(false)}
        currentBalance={walletBalance}
        currency={currency}
        onSuccess={() => {
          if (refetchWallet) refetchWallet();
        }}
      />
    </MainContainer>
  );
};

export default CashManagementContainer;
